import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Flag, Info, Lock, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { crossBorderAPI } from '../../api';

const DOC_STYLE = {
  Missing: 'bg-red-100 text-red-800', Partial: 'bg-amber-100 text-amber-800', Complete: 'bg-emerald-100 text-emerald-800', 'Not Applicable': 'bg-slate-100 text-slate-600',
};
const DOC_ICON = { Missing: AlertTriangle, Partial: Clock, Complete: CheckCircle2, 'Not Applicable': Info };

// Compact cross-border info for kanban cards: docs readiness, elapsed border time, next action, owner, flags
export const CbCardInfo = ({ cb, loadId }) => {
  if (!cb) return null;
  const DocIcon = DOC_ICON[cb.docs.state] || Info;
  return (
    <div className="mt-2 pt-2 border-t border-[var(--tms-border)] space-y-1 text-[11px]" data-testid={`cb-card-info-${loadId}`}>
      <div className="flex items-center gap-1 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold ${DOC_STYLE[cb.docs.state]}`} title={cb.docs.summary} data-testid={`cb-card-docs-${loadId}`}>
          <DocIcon className="w-3 h-3" /> Docs {cb.docs.state}
        </span>
        {cb.time_at_border && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold ${cb.overdue ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`} title="Time at current border side" data-testid={`cb-card-border-time-${loadId}`}>
            <Clock className="w-3 h-3" /> Border {cb.time_at_border.side} {cb.time_at_border.label}
          </span>
        )}
        {cb.override && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold bg-slate-200 text-slate-700" title={`Override: ${cb.override.reason}`}><Lock className="w-3 h-3" /> Override</span>}
        {cb.indicator && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-800" data-testid={`cb-card-indicator-${loadId}`}><Flag className="w-3 h-3" /> {cb.indicator}</span>}
        {cb.has_errors && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-800" title={cb.conflicts.filter(c => c.severity === 'error').map(c => c.message).join('\n')} data-testid={`cb-card-conflict-${loadId}`}><AlertTriangle className="w-3 h-3" /> Inconsistent</span>}
      </div>
      <div className="flex items-center justify-between gap-2 text-[var(--tms-text-secondary)]">
        <span className="truncate" title={cb.next_action}>Next: <strong>{cb.next_action}</strong></span>
        <span className="inline-flex items-center gap-1 shrink-0 text-[var(--tms-text-muted)]"><UserRound className="w-3 h-3" /> {cb.owner || 'Unassigned'}</span>
      </div>
    </div>
  );
};

// Review queue: legacy/unconfirmed stages and inconsistent records
export const CbReviewQueueDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    crossBorderAPI.reviewQueue().then(r => setData(r.data)).catch(() => setData({ count: 0, items: [], error: true }));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cb-review-queue-dialog">
        <DialogHeader><DialogTitle>Cross-Border review queue {data ? `(${data.count})` : ''}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-[var(--tms-text-muted)]">Loading…</p> : data.items.length === 0 ? <p className="text-sm text-[var(--tms-text-muted)]">Nothing to review.</p> : (
          <div className="divide-y divide-[var(--tms-border)]">
            {data.items.map(item => (
              <button key={item.id} className="w-full text-left py-2 hover:bg-[var(--tms-hover)] px-1 rounded" onClick={() => { onOpenChange(false); navigate(`/loads/${item.id}`); }} data-testid={`cb-review-item-${item.id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono font-bold">{item.load_number}</span>
                  <span className="text-xs text-[var(--tms-text-muted)]">{item.customer_name} · {item.status} · {item.stage}</span>
                </div>
                {item.indicator && <div className="text-xs text-amber-800 flex items-center gap-1"><Flag className="w-3 h-3" /> {item.indicator}</div>}
                {item.conflicts.filter(c => c.severity !== 'info').map((c, i) => (
                  <div key={i} className={`text-xs ${c.severity === 'error' ? 'text-red-800' : 'text-amber-800'}`}>• {c.message} <span className="text-[var(--tms-text-muted)]">{c.action}</span></div>
                ))}
                <div className="text-[11px] text-[var(--tms-text-muted)]">Next: {item.next_action} · {item.owner || 'Unassigned'}</div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
